<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { watch } from 'vue';
import {
  CustomMetadataService,
  type EntityCustomMetadataDto,
  type EntityCustomMetadataEntryLabelDto,
  type GetCustomMetadataListResponseDto,
  type GetCustomMetadataResponseDto,
  type GetCustomMetadataEntryLabelValueResponseDto,
} from '@/api';
import SChip from '@/components/SChip.vue';

interface CustomMetadataLabelSelectorProps {
  metadata: GetCustomMetadataListResponseDto;
  entityCustomMetadata?: EntityCustomMetadataDto;
}
const props = defineProps({
  metadata: null,
  entityCustomMetadata: null
});

type CustomMetadataLabelSelectorEmits = {
  (event: 'update', metadataId: string, entries: EntityCustomMetadataEntryLabelDto[]): void,
}
const emits = defineEmits(["update"]);

let loading = _ref<boolean>(false);
// Local v-model proxy
let selectedEntries = _ref<string[] | string | null>([]);
let fetchedMetadata = _ref<GetCustomMetadataResponseDto>();
let searchString = _ref<string>('');

const allowMultiple = _computed(() => {
  if (fetchedMetadata.value) {
    return fetchedMetadata.value.isAllowMultipleValues;
  }
  return false;
});

const selectorItems = _computed(() => {
  if (fetchedMetadata.value) {
    return fetchedMetadata.value.entries.data as GetCustomMetadataEntryLabelValueResponseDto[];
  }
  return [];
});

/**
 * Get details on the custom metadata
 * @param metadataId
 */
const fetchMetadataInfos = async (metadataId: string) => {
  loading.value = true;
  fetchedMetadata.value = await CustomMetadataService.getCustomMetadata({ id: metadataId });
  loading.value = false;
};

/**
 * Handle selector update
 * Re-map selected entries so we also get type, ID and value from the fetched metadata on emitted change
 * @param newSelectedEntries
 */
const onSelectorUpdate = (newSelectedEntries: string[]) => {
  const entries = Array.isArray(newSelectedEntries) ? newSelectedEntries : [newSelectedEntries];
  const selectedEntriesWithValues: EntityCustomMetadataEntryLabelDto[] = entries
    .map((entryId) => selectorItems.value.find((item) => item.id === entryId) as EntityCustomMetadataEntryLabelDto | undefined)
    .filter((entry): entry is EntityCustomMetadataEntryLabelDto => entry !== undefined);
  emits('update', props.metadata.id, selectedEntriesWithValues);
  searchString.value = '';
};

watch(() => props.metadata, (newMetadata) => {
  if (newMetadata) {
    fetchMetadataInfos(newMetadata.id);
  }
}, { immediate: true });

// Pre-set selected entries based on the entity custom metadata
watch([() => props.entityCustomMetadata, () => allowMultiple.value], ([newEntityCustomMetadata, newAllowMultiple]) => {
  if (newEntityCustomMetadata) {
    if (newAllowMultiple) {
      selectedEntries.value = newEntityCustomMetadata.entries.map((entry) => entry.id);
    } else {
      // Metadata cannot be multiple: only take first entry
      const [firstEntry] = newEntityCustomMetadata.entries as GetCustomMetadataEntryLabelValueResponseDto[];
      selectedEntries.value = firstEntry?.id || null;
    }
  }
}, { immediate: true });

</script>

<template lang="pug">
.custom-metadata-label-selector
    v-autocomplete.mt-1(
      :disabled="!fetchedMetadata || loading"
      clearable
      item-text="value"
      item-value="id"
      :items="selectorItems"
      :placeholder="$t('custom_metadata.selectors.label_placeholder', { name: props.metadata.name.toLowerCase() })"
      :menu-props="{ bottom: true, offsetY: true }"
      dense outlined
      :multiple="allowMultiple"
      hide-details
      :search-input.sync="searchString"
      data-track="custom-metadata-label-selector"
      v-model="selectedEntries"
      @change="onSelectorUpdate"
    )
      template(v-slot:item="{ item, attrs, on }")
        v-simple-checkbox(v-if="allowMultiple" :value="attrs.inputValue" v-on="on")
        SChip(
          :text="item.value"
          data-track="custom-metadata-label-selector-item"
          plain
        )

      template(v-slot:selection="{ item }")
        SChip.mr-2(
          :text="item.value"
          data-track="custom-metadata-label-selector-item-selected"
          plain
        )

      template(v-slot:no-data)
        .pa-2.text-center No options available:

</template>
